import { inject, Injectable, OnDestroy } from '@angular/core';
import { App } from '@df/core/app';
import { NPrivacyState, PrivacyStateService } from '@df/core/meta/privacy-state.service';
import { WINDOW } from '@df/dom/globals';
import { PrivacyService } from '@df/module-privacy/privacy.service';
import { injectTimeoutService } from '@df/ng/timeout.service';
import { onetrustLoad } from './onetrust-load';
import { NOnetrust } from './onetrust.interfaces';

@Injectable({
    providedIn: 'root'
})
export class OneTrustService implements OnDestroy {
    private window = inject(WINDOW);
    private app = inject(App);
    private timeoutService = injectTimeoutService();
    private privacyService = inject(PrivacyService);
    private privacyStateService = inject(PrivacyStateService);
    private options = inject(NOnetrust.OPTIONS);
    /**
     * Cleanup callback
     */
    private cleanup?: () => void;

    /**
     * App initialize
     */
    async appInitialize() {
        if (this.app.isPlatformBrowser) {
            await onetrustLoad();
            const cb = event => this.applySelection(event);
            this.window.addEventListener('OneTrustGroupsUpdated', cb);
            this.cleanup = () => this.window.removeEventListener('OneTrustGroupsUpdated', cb);
        }
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        this.cleanup?.();
    }

    /**
     * Toggle OneTrust info display
     */
    toggleInfoDisplay(): void {
        try {
            if (this.app.isPlatformBrowser) {
                this.api.ToggleInfoDisplay();
            }
        } catch (error) {
            console.error(error);
        }
    }

    /**
     * Load OneTrust banner
     */
    loadBanner(): void {
        try {
            if (this.app.isPlatformBrowser) {
                const oneTrust = this.api;
                oneTrust.Init();
                this.timeoutService.setTimeout(() => oneTrust.LoadBanner(), 1000);
            }
        } catch (error) {
            console.error(error);
        }
    }

    /**
     * Get OneTrust API
     */
    private get api(): NOnetrust.IApi {
        if (!window.OneTrust) {
            throw new Error('OneTrust is not initialized');
        }
        return window.OneTrust;
    }

    /**
     * Sync df core privacy data with OneTrust selection
     */
    private applySelection(event: { detail: string[] }): void {
        const allowedGroups: string[] | undefined = event?.detail;

        if (!allowedGroups) {
            return;
        }

        const allowedAreas = allowedGroups
            .map(id => this.options.rules[id]?.toString() as unknown as NPrivacyState.Area)
            .filter(area => area !== undefined);

        this.privacyStateService.getAreas().forEach(area => {
            this.privacyStateService.setValue(area, allowedAreas.includes(area));
        });

        const consent = this.api.IsAlertBoxClosedAndValid();

        if (consent) {
            this.privacyService.apply(this.privacyStateService.getSelection());
        } else {
            this.privacyStateService.setCheckStatus(NPrivacyState.Status.modal);
        }
    }
}
